import axios, { AxiosInstance } from 'axios';
import { API_CONFIG, getHeaders } from './config';

// Types matching the Cloudflare D1 backend schema (snake_case)
export interface BackendArticle {
  id: number;
  name: string;
  slug?: string;
  upload_date: string;  // ISO string from D1
  rss_date?: string;
  body: string;
  tags?: string[] | string;  // Can be JSON string or array
  description?: string;
  cover_image?: string;
  hidden?: number;  // D1 uses 0/1 for boolean
  uploads?: any[];
}

// Types matching practicalmedia.com frontend expectations
export interface Article {
  id: number;
  title: string;
  name: string;
  slug?: string;
  meta_title?: string;
  meta_description?: string;
  uploadDate: string;
  description?: string;
  tags?: string[];
  keywords?: string[];
  primary_keyword?: string;
  secondary_keywords?: string[];
  cover_image?: string;
  featured?: boolean;
  status?: string;
  content?: string;
  body?: string;
}

export interface ArticleWithBody extends Article {
  content: string;
  body: string;
}

class APIAdapter {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: getHeaders(),
    });
  }

  // Transform backend article to frontend format
  private async transformArticle(backendArticle: BackendArticle): Promise<Article> {
    try {
      // Cover image URL - API returns full URL already
      let coverImageUrl: string | undefined;

      if (backendArticle.cover_image) {
        // API returns full URL, use directly
        coverImageUrl = backendArticle.cover_image;
      }

      // Use name as title since backend doesn't have separate title field
      const title = this.formatTitle(backendArticle.name);

      // Handle tags - can be JSON string from D1 or already an array
      let tags: string[] = [];
      if (backendArticle.tags) {
        if (typeof backendArticle.tags === 'string') {
          try {
            tags = JSON.parse(backendArticle.tags);
          } catch {
            tags = [];
          }
        } else {
          tags = backendArticle.tags;
        }
      }

      // Unescape literal \n sequences into real newlines for markdown rendering
      const body = backendArticle.body
        ? backendArticle.body.replace(/\\n/g, '\n')
        : '';

      return {
        id: backendArticle.id,
        title: title,
        name: backendArticle.name || '',
        slug: backendArticle.slug || backendArticle.name,
        meta_title: title,
        meta_description: backendArticle.description,
        uploadDate: backendArticle.upload_date || new Date().toISOString(),
        description: backendArticle.description,
        tags: tags,
        keywords: tags,
        primary_keyword: tags[0],
        secondary_keywords: tags.slice(1),
        cover_image: coverImageUrl,
        featured: false,
        status: backendArticle.hidden ? 'draft' : 'published',
        content: body,
        body: body,
      };
    } catch (error) {
      console.error('Transform error for article:', backendArticle.id, error);
      throw error;
    }
  }

  private formatTitle(name: string | undefined): string {
    if (!name) return 'Untitled';
    // Convert URL-friendly name to title format
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Article endpoints
  async getArticles(_search?: string, tags?: string, page?: number): Promise<Article[]> {
    try {
      const params = new URLSearchParams();
      if (tags) {
        params.append('tags', tags);
      }
      if (page) {
        params.append('page', String(page));
      }

      const response = await this.client.get('/articles', { params });

      // Handle both array and paginated responses
      const articles = Array.isArray(response.data)
        ? response.data
        : response.data.articles || [];

      const transformedArticles = [];
      for (const article of articles) {
        if (!article.hidden) {
          try {
            const transformed = await this.transformArticle(article);
            transformedArticles.push(transformed);
          } catch (error) {
            console.error('Error transforming article:', article.id, error);
          }
        }
      }
      return transformedArticles;
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw error;
    }
  }

  async getArticle(id: number): Promise<ArticleWithBody> {
    try {
      // Cloudflare Workers API uses /articles/:id
      const response = await this.client.get(`/articles/${id}`);

      const articleData = response.data;

      if (!articleData) {
        throw new Error('Article not found');
      }

      const article = await this.transformArticle(articleData);
      return {
        ...article,
        content: article.body || '',
        body: article.body || '',
      } as ArticleWithBody;
    } catch (error) {
      console.error('Error fetching article:', error);
      throw error;
    }
  }

  async getArticleByName(name: string): Promise<ArticleWithBody> {
    try {
      // Cloudflare Workers API uses /articles/by-slug/:slug
      const response = await this.client.get(`/articles/by-slug/${encodeURIComponent(name)}`);

      const article = await this.transformArticle(response.data);
      return {
        ...article,
        content: article.body || '',
        body: article.body || '',
      } as ArticleWithBody;
    } catch (error) {
      console.error('Error fetching article by name:', error);
      throw error;
    }
  }

  async getArticleBySlug(slug: string): Promise<ArticleWithBody> {
    try {
      // Cloudflare Workers API uses /articles/by-slug/:slug
      const response = await this.client.get(`/articles/by-slug/${encodeURIComponent(slug)}`, {
        validateStatus: (status: number) => status < 500, // Don't throw on 301/404
      });

      // Handle redirects from API
      if (response.data?.redirect) {
        window.location.replace(response.data.redirect);
        throw new Error('Redirecting');
      }

      if (response.status === 410 || response.status === 404) {
        window.location.replace('/blog');
        throw new Error('Article not found');
      }

      const article = await this.transformArticle(response.data);
      return {
        ...article,
        content: article.body || '',
        body: article.body || '',
      } as ArticleWithBody;
    } catch (error) {
      console.error('Error fetching article by slug:', error);
      throw error;
    }
  }

  async getLatestArticles(): Promise<Article[]> {
    try {
      const response = await this.client.get('/articles/latest');
      const transformedArticles = await Promise.all(
        response.data.map((article: BackendArticle) => this.transformArticle(article))
      );
      return transformedArticles;
    } catch (error) {
      console.error('Error fetching latest articles:', error);
      throw error;
    }
  }

  async getArticlesByTags(tags: string[]): Promise<Article[]> {
    try {
      const response = await this.client.get('/articles/by_tags', {
        params: { tags: tags.join(',') }
      });
      const transformedArticles = await Promise.all(
        response.data.map((article: BackendArticle) => this.transformArticle(article))
      );
      return transformedArticles;
    } catch (error) {
      console.error('Error fetching articles by tags:', error);
      throw error;
    }
  }

  async getCoverImage(name: string): Promise<string> {
    try {
      const response = await this.client.get('/articles/cover', {
        params: { name }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching cover image:', error);
      throw error;
    }
  }

}

export const apiAdapter = new APIAdapter();
export const articlesApi = {
  getArticles: (search?: string, tags?: string, page?: number) => apiAdapter.getArticles(search, tags, page),
  getArticle: (id: number) => apiAdapter.getArticle(id),
  getArticleByName: (name: string) => apiAdapter.getArticleByName(name),
  getArticleBySlug: (slug: string) => apiAdapter.getArticleBySlug(slug),
  getLatestArticles: () => apiAdapter.getLatestArticles(),
};